import { Box } from '@mui/material';

/**
 * Custom SVG illustration shown on the empty state of the course-templates
 * dashboard. Designed to evoke "blueprint" / "course in design phase":
 *
 *   - Stacked paper sheets fanned out
 *   - A bookmark + scripture-ribbon to signal seminary context
 *   - A gentle floating cursor / sparkle to suggest authoring + AI assist
 *
 * Inline SVG so it can theme to the current palette via `currentColor` and
 * doesn't add a network request.
 */
export function EmptyTemplatesIllustration(): JSX.Element {
  return (
    <Box
      sx={{
        width: 220,
        height: 180,
        mx: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        viewBox="0 0 220 180"
        width="220"
        height="180"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Soft background blob */}
        <ellipse cx="110" cy="160" rx="80" ry="8" fill="currentColor" opacity="0.06" />

        {/* Back paper sheet (rotated) */}
        <g transform="rotate(-8 110 90)">
          <rect
            x="50"
            y="35"
            width="120"
            height="120"
            rx="6"
            fill="currentColor"
            opacity="0.06"
            stroke="currentColor"
            strokeOpacity="0.18"
            strokeWidth="1.2"
          />
        </g>

        {/* Middle paper sheet */}
        <g transform="rotate(3 110 90)">
          <rect
            x="55"
            y="30"
            width="120"
            height="120"
            rx="6"
            fill="currentColor"
            opacity="0.08"
            stroke="currentColor"
            strokeOpacity="0.22"
            strokeWidth="1.2"
          />
        </g>

        {/* Front paper sheet */}
        <rect
          x="50"
          y="28"
          width="120"
          height="120"
          rx="6"
          fill="#fff"
          stroke="currentColor"
          strokeOpacity="0.32"
          strokeWidth="1.4"
        />

        {/* Header bar */}
        <rect x="60" y="40" width="64" height="6" rx="3" fill="currentColor" opacity="0.5" />
        <rect x="60" y="52" width="40" height="4" rx="2" fill="currentColor" opacity="0.25" />

        {/* Body lines */}
        <rect x="60" y="70" width="100" height="3" rx="1.5" fill="currentColor" opacity="0.18" />
        <rect x="60" y="78" width="92" height="3" rx="1.5" fill="currentColor" opacity="0.18" />
        <rect x="60" y="86" width="80" height="3" rx="1.5" fill="currentColor" opacity="0.18" />

        {/* Module blocks */}
        <rect x="60" y="100" width="44" height="20" rx="3" fill="currentColor" opacity="0.12" />
        <rect x="108" y="100" width="44" height="20" rx="3" fill="currentColor" opacity="0.12" />
        <rect x="60" y="124" width="92" height="14" rx="3" fill="currentColor" opacity="0.08" />

        {/* Ribbon bookmark on the right edge */}
        <path
          d="M 162 28 L 168 28 L 168 54 L 165 50 L 162 54 Z"
          fill="currentColor"
          opacity="0.7"
        />

        {/* Sparkle (top right of front sheet) */}
        <g transform="translate(150 22)">
          <path
            d="M 4 0 L 5 3 L 8 4 L 5 5 L 4 8 L 3 5 L 0 4 L 3 3 Z"
            fill="currentColor"
            opacity="0.85"
          />
        </g>
        <g transform="translate(180 60)">
          <path
            d="M 3 0 L 3.7 2.3 L 6 3 L 3.7 3.7 L 3 6 L 2.3 3.7 L 0 3 L 2.3 2.3 Z"
            fill="currentColor"
            opacity="0.5"
          />
        </g>

        {/* Pencil tip (suggesting authoring) */}
        <g transform="translate(140 110) rotate(-30)">
          <rect x="0" y="0" width="36" height="6" rx="1.5" fill="currentColor" opacity="0.4" />
          <polygon points="36,0 44,3 36,6" fill="currentColor" opacity="0.6" />
        </g>
      </svg>
    </Box>
  );
}
